import { formatJobNumber, inboxListSchema } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  FilterChip,
  Page,
  PageHeader,
  Pagination,
  SearchBox,
  Select,
  SkeletonRows,
  buttonClass,
} from '../components/ui.js'
import { BRAND } from '../lib/brand.js'
import { cn } from '../lib/cn.js'
import { formatDateTime, formatTime, relativeTime } from '../lib/format.js'
import { useJobOverlay } from '../lib/job-overlay.js'
import { useSession } from '../lib/session.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../lib/trpc.js'
import { useUrlState } from '../lib/url-state.js'
import { EmailCompose } from '../components/email/EmailCompose.js'
import { ScheduledSendCard, useAccountZone } from '../components/compose/ScheduledSendCard.js'

/**
 * The live inbox, as two panes.
 *
 * Inbound email, SMS and MMS land here from the provider webhooks, and the
 * NOTIFY trigger pushes them onto this screen without a refresh — which is
 * the whole point: an enquiry that sits unseen for two hours is a job that
 * went to whoever answered first.
 *
 * With nothing open the list is the whole width, one message to a line —
 * sender, subject and preview, channel, client, when — so a morning's mail is
 * one screen rather than a narrow column beside an empty pane. Opening one
 * narrows the list to 344px and the reading pane takes the rest. Below the
 * desk there is only room for one, so the pane that shows is whichever the
 * reader is in — list until a message is picked, message until it is closed.
 * Which message is open lives in the URL, so "look at this one" is a link.
 */
export function Inbox() {
  const trpc = useTRPC()
  const { can } = useSession()
  const [params, setParams] = useUrlState(inboxListSchema)
  const [composing, setComposing] = useState(false)
  const [showScheduled, setShowScheduled] = useState(false)
  const scheduled = useQuery({ ...trpc.compose.scheduled.queryOptions({}), retry: false })
  const waiting = scheduled.data ?? []
  const failedCount = waiting.filter((row) => row.status === 'FAILED').length
  const timeZone = useAccountZone()

  const { data, isPending, error, refetch } = useQuery(trpc.inbox.list.queryOptions(params))
  const { data: unread } = useQuery(trpc.inbox.unreadCount.queryOptions())

  const items = data?.items ?? []
  const selected = items.find((message) => message.id === params.selected)

  // A selection that survived a filter change points at a message no longer in
  // the list, and the reading pane would hold a thread the list cannot show.
  useEffect(() => {
    if (params.selected && data && !selected) {
      setParams({ selected: undefined }, { replace: true })
    }
  }, [params.selected, data, selected, setParams])

  const select = (id: string | undefined) => setParams({ selected: id }, { replace: true })

  const toggleStatus = (status: 'UNREAD' | 'READ' | 'ARCHIVED') =>
    setParams({
      status: params.status.includes(status)
        ? params.status.filter((entry) => entry !== status)
        : [...params.status, status],
      page: 1,
    })

  const toggleChannel = (channel: 'EMAIL' | 'SMS') =>
    setParams({
      channel: params.channel.includes(channel)
        ? params.channel.filter((entry) => entry !== channel)
        : [...params.channel, channel],
      page: 1,
    })

  return (
    <Page width="xwide">
      <PageHeader
        title="Inbox"
        description={
          unread === undefined
            ? 'Email and SMS from customers'
            : `${unread} unread · email and SMS from customers`
        }
        actions={
          // On a phone the filters are the list's own controls, so they stay
          // with the list rather than leaving with the desktop title.
          <div className={cn('flex flex-wrap gap-1.5', selected && 'hidden desk:flex')}>
            <FilterChip
              active={params.status.includes('UNREAD')}
              onClick={() => toggleStatus('UNREAD')}
            >
              Unread
            </FilterChip>
            <FilterChip
              active={params.channel.includes('EMAIL')}
              onClick={() => toggleChannel('EMAIL')}
            >
              Email
            </FilterChip>
            <FilterChip
              active={params.channel.includes('SMS')}
              onClick={() => toggleChannel('SMS')}
            >
              SMS
            </FilterChip>
            <FilterChip
              active={params.unlinkedOnly}
              onClick={() => setParams({ unlinkedOnly: !params.unlinkedOnly, page: 1 })}
            >
              Unlinked
            </FilterChip>
            <FilterChip
              active={params.status.includes('ARCHIVED')}
              onClick={() => toggleStatus('ARCHIVED')}
            >
              Archived
            </FilterChip>
            {waiting.length > 0 || showScheduled ? (
              <FilterChip active={showScheduled} onClick={() => setShowScheduled((shown) => !shown)}>
                Scheduled · {waiting.length}
                {failedCount > 0 ? ` (${failedCount} failed)` : ''}
              </FilterChip>
            ) : null}
          </div>
        }
      />

      {/* The two panes fill the screen at a desk, so the reading pane is a
          place rather than a card that stops halfway down. */}
      <div
        className={cn(
          'flex items-stretch gap-[var(--stack-gap)]',
          selected && 'desk:min-h-[calc(100dvh-5.5rem)]',
        )}
      >
        <Card
          className={cn(
            'min-w-0 flex-1',
            // One pane at a time on a narrow screen, and the message wins.
            selected && 'hidden desk:block desk:w-[344px] desk:flex-none',
          )}
        >
          <div className="border-b border-ink-200 bg-white px-[var(--row-pad-x)] py-2">
            <div className="flex items-center gap-2">
              <SearchBox
                value={params.q ?? ''}
                onChange={(q) => setParams({ q: q || undefined, page: 1 }, { replace: true })}
                placeholder="Search messages…"
                className={cn('w-full min-w-0', selected ? 'sm:w-full' : 'sm:w-80')}
              />
              {can('INBOX_MANAGE') ? (
                <Button
                  size="sm"
                  variant="primary"
                  className={cn('shrink-0', !selected && 'ml-auto')}
                  onClick={() => setComposing(true)}
                >
                  New email
                </Button>
              ) : null}
            </div>
          </div>

          {showScheduled ? (
            waiting.length === 0 ? (
              <EmptyState title="Nothing scheduled" description="Email set to send later waits here until it goes." />
            ) : (
              <ul aria-label="Scheduled to send" className="flex flex-col gap-2 p-3">
                {waiting.map((row) => (
                  <li key={row.id}>
                    <ScheduledSendCard row={row} timeZone={timeZone} showJob />
                  </li>
                ))}
              </ul>
            )
          ) : error ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <SkeletonRows />
          ) : items.length === 0 ? (
            <EmptyState
              icon="inbox"
              title="Nothing here"
              description="Email and SMS from customers arrive here the moment they land."
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {items.map((message) => (
                <li key={message.id}>
                  <ThreadRow
                    message={message}
                    selected={message.id === params.selected}
                    wide={!selected}
                    onSelect={() => select(message.id)}
                  />
                </li>
              ))}
            </ul>
          )}

          {data && !showScheduled ? (
            <Pagination
              page={data.page}
              pageCount={data.pageCount}
              total={data.total}
              onChange={(page) => setParams({ page })}
            />
          ) : null}
        </Card>

        {selected ? <Reading id={selected.id} onClose={() => select(undefined)} /> : null}
      </div>

      <EmailCompose open={composing} onClose={() => setComposing(false)} />
    </Page>
  )
}

type InboxListItem = RouterOutputs['inbox']['list']['items'][number]

const CHANNEL_TONE = { EMAIL: 'blue', SMS: 'purple', MMS: 'purple' } as const

/** Email is blue, everything that arrives down a phone line is purple. */
function channelTone(channel: string) {
  return CHANNEL_TONE[channel as keyof typeof CHANNEL_TONE] ?? 'neutral'
}

/**
 * One thread in the list.
 *
 * Unread carries a green dot and a semibold sender; read drops to 400 weight
 * and an ink-300 dot. The dot is the only thing scanned at this width, so it
 * has to survive the sender name being long enough to truncate.
 */
function ThreadRow({
  message,
  selected,
  wide,
  onSelect,
}: {
  message: InboxListItem
  selected: boolean
  /** The list has the whole width: one line per message. */
  wide: boolean
  onSelect: () => void
}) {
  const unread = message.status === 'UNREAD'
  const sender =
    message.direction === 'OUTBOUND'
      ? `To ${message.toAddresses[0] ?? message.fromAddress}`
      : (message.fromName ?? message.fromAddress)
  const preview = message.strippedText ?? message.bodyText
  const dot = (
    <span
      className={cn('block size-1.5 shrink-0 rounded-full', unread ? 'bg-emerald-500' : 'bg-ink-300')}
    />
  )
  const rowClass = cn(
    'w-full px-[var(--row-pad-x)] py-[var(--row-pad-y)] text-left transition-colors',
    selected ? 'bg-ink-100' : unread ? 'bg-brand-50/50 hover:bg-brand-50' : 'hover:bg-ink-50',
  )

  if (wide) {
    return (
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className={cn(
          rowClass,
          'flex flex-wrap items-center gap-x-3 gap-y-0.5 sm:grid sm:grid-cols-[0.375rem_minmax(0,13rem)_minmax(0,1fr)_3.5rem_minmax(0,11rem)_6.5rem]',
        )}
      >
        {dot}
        <span
          className={cn('min-w-0 truncate text-sm text-ink-900', unread ? 'font-semibold' : 'font-normal')}
        >
          {sender}
        </span>
        <span className="min-w-0 basis-full truncate text-xs sm:basis-auto">
          <span className={cn('text-ink-900', unread && 'font-medium')}>
            {message.subject ?? (preview ? '' : '(no subject)')}
          </span>
          {preview && preview !== message.subject ? (
            <span className="text-ink-500">
              {message.subject ? ' — ' : ''}
              {preview}
            </span>
          ) : null}
        </span>
        <span>
          <Badge tone={channelTone(message.channel)}>{message.channel}</Badge>
        </span>
        <span className="min-w-0 truncate text-xs text-ink-500">
          {message.client?.name ?? <span className="text-ink-400">No client matched</span>}
        </span>
        <span className="text-right text-xs tabular-nums text-ink-400">
          {relativeTime(message.receivedAt)}
        </span>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? 'true' : undefined}
      className={cn(rowClass, 'flex gap-2.5')}
    >
      <span className="w-1.5 shrink-0 pt-[7px]">{dot}</span>

      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm text-ink-900',
              unread ? 'font-semibold' : 'font-normal',
            )}
          >
            {sender}
          </span>
          <span className="shrink-0 text-xs tabular-nums text-ink-400">
            {relativeTime(message.receivedAt)}
          </span>
        </span>

        <span className="mt-0.5 flex items-center gap-1.5">
          <Badge tone={channelTone(message.channel)}>{message.channel}</Badge>
          <span className="min-w-0 truncate text-xs text-ink-700">
            {message.subject ?? preview ?? '(no subject)'}
          </span>
        </span>
        <span className="block truncate text-[11px] text-ink-400">
          {message.client?.name ?? 'No client matched'}
        </span>
      </span>
    </button>
  )
}

/**
 * The reading pane.
 *
 * Refetched by id rather than reusing the list row, because the list carries a
 * stripped preview and this needs the whole body, the attachments and the job
 * the message is already linked to.
 */
function Reading({ id, onClose }: { id: string; onClose: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { hrefFor, openJob } = useJobOverlay()
  const { can } = useSession()

  const { data, isPending, error, refetch } = useQuery(trpc.inbox.byId.queryOptions({ id }))

  const invalidate = () => {
    void queryClient.invalidateQueries(trpc.inbox.pathFilter())
  }

  const [replying, setReplying] = useState(false)
  const setStatus = useMutation(trpc.inbox.setStatus.mutationOptions({ onSuccess: invalidate }))
  const convert = useMutation(
    trpc.inbox.convertToJob.mutationOptions({
      onSuccess: (result) => {
        invalidate()
        // Opens over the inbox, so the message that became a job is still
        // behind it when the job is closed.
        openJob(result.jobId)
      },
    }),
  )

  return (
    <Card className="flex min-w-0 flex-1 flex-col">
      {error ? (
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      ) : isPending ? (
        <SkeletonRows rows={4} />
      ) : (
        <>
          <div className="border-b border-ink-200 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink-900">
                  {data.subject ?? '(no subject)'}
                </p>
                <p className="mt-0.5 truncate text-xs text-ink-500">
                  {data.fromName ? `${data.fromName} <${data.fromAddress}>` : data.fromAddress} ·{' '}
                  {formatDateTime(data.receivedAt)}
                </p>
              </div>

              <div className="flex shrink-0 gap-1.5">
                <Button size="sm" variant="ghost" className="desk:hidden" onClick={onClose}>
                  Close
                </Button>
                {data.job ? (
                  <Link to={hrefFor(data.job.id)} className={buttonClass({ size: 'sm' })}>
                    Open job
                  </Link>
                ) : can('INBOX_MANAGE') && can('JOB_EDIT') ? (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={convert.isPending}
                    // The server refuses a message with no client, and says so.
                    disabled={!data.client}
                    title={data.client ? undefined : 'Attach this message to a client first'}
                    onClick={() => convert.mutate({ messageId: data.id })}
                  >
                    Create job
                  </Button>
                ) : null}
              </div>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <Badge tone={channelTone(data.channel)}>{data.channel}</Badge>
              {data.job ? (
                <Badge tone="green">{formatJobNumber(data.job)}</Badge>
              ) : (
                // Amber because it is the dispatcher's job to fix: linking a
                // thread to work is the whole point of this screen.
                <Badge tone="amber">Not linked</Badge>
              )}
              <span className="text-xs text-ink-400">
                {data.client ? `Client: ${data.client.name}` : 'Matched client: none'}
                {data.attachments.length > 0
                  ? ` · ${data.attachments.length} ${data.attachments.length === 1 ? 'attachment' : 'attachments'}`
                  : ''}
              </span>
            </div>
          </div>

          <div className="flex-1 bg-ink-50 p-4">
            <div className="max-w-[520px] rounded-2xl bg-white p-3.5 ring-1 ring-ink-200">
              <p className="text-xs font-medium text-ink-500">
                {data.fromName ?? data.fromAddress} · {formatTime(data.receivedAt)}
              </p>
              <p className="mt-1.5 text-sm whitespace-pre-wrap text-ink-800">
                {data.strippedText ?? data.bodyText ?? '(no message body)'}
              </p>
            </div>
          </div>

          {can('INBOX_MANAGE') ? (
            <div className="flex flex-wrap items-center gap-2 border-t border-ink-200 px-4 py-3">
              <Button
                size="md"
                variant="ghost"
                loading={setStatus.isPending}
                onClick={() =>
                  setStatus.mutate({
                    id: data.id,
                    status: data.status === 'UNREAD' ? 'READ' : 'UNREAD',
                  })
                }
              >
                Mark {data.status === 'UNREAD' ? 'read' : 'unread'}
              </Button>
              {data.status === 'ARCHIVED' ? null : (
                <Button
                  size="md"
                  variant="ghost"
                  onClick={() => setStatus.mutate({ id: data.id, status: 'ARCHIVED' })}
                >
                  Archive
                </Button>
              )}
              <Button size="md" variant="primary" className="ml-auto" onClick={() => setReplying(true)}>
                Reply
              </Button>
            </div>
          ) : null}
          {data ? (
            <EmailCompose
              open={replying}
              onClose={() => setReplying(false)}
              title="Reply"
              jobId={data.jobId}
              clientId={data.client?.id}
              replyToMessageId={data.id}
              files={data.attachments.map((row) => ({
                id: row.attachment.id,
                name: row.attachment.filename,
              }))}
              initial={{
                to: [
                  data.direction === 'OUTBOUND'
                    ? (data.toAddresses[0] ?? data.fromAddress)
                    : data.fromAddress,
                ],
                subject: data.subject?.toLowerCase().startsWith('re:')
                  ? data.subject
                  : `Re: ${data.subject ?? '(no subject)'}`,
                body: quoted(data),
              }}
            />
          ) : null}
        </>
      )}
    </Card>
  )
}

export function quoted(message: {
  fromName: string | null
  fromAddress: string
  strippedText: string | null
  bodyText: string | null
  receivedAt: Date | string
}): string {
  const text = message.strippedText ?? message.bodyText
  if (!text) return ''
  const when = formatDateTime(message.receivedAt)
  const who = message.fromName ?? message.fromAddress
  const lines = text.split('\n').map((line) => `> ${line}`)
  return `\n\nOn ${when}, ${who} wrote:\n${lines.join('\n')}`
}
